<template>
  <div v-editable="blok" class="section-item" @click="openLink">
    <img
      v-if="blok.backgroundImage && blok.backgroundImage?.filename.length > 0"
      :src="blok.backgroundImage.filename"
      :alt="blok.backgroundImage.alt || ''"
      class="section-item-image"
    />
    <component
      :is="blok.size || 'h1'"
      class="section-item-title"
      :class="blok.titleBackground ? 'with-background' : ''"
    >
      {{ blok.title }}
    </component>
  </div>
</template>

<script setup lang="ts">
import type { StoryblokSectionItemBlok } from '@/features/storyblok/types';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { CommonRoutesName } from '@/router/constants/routesName';
import {
  extractStorySlugFromWardPath,
  resolveStoryblokLink,
} from '@/features/storyblok/utils/link';
const router = useRouter();

const props = defineProps<{
  blok: StoryblokSectionItemBlok;
}>();

const targetLink = computed(() => resolveStoryblokLink(props.blok.link, props.blok.linkType));
const openLink = () => {
  if (!targetLink.value) {
    return;
  }

  if (targetLink.value.type === 'external') {
    window.open(targetLink.value.href, '_blank', 'noopener,noreferrer');
    return;
  }

  if (targetLink.value.type === 'storyblok') {
    void router.replace({
      name: CommonRoutesName.StoryblokPage,
      query: { slug: targetLink.value.slug },
    });
    return;
  }

  const storySlugFromInternalPath = extractStorySlugFromWardPath(targetLink.value.path);
  if (storySlugFromInternalPath) {
    void router.replace({
      name: CommonRoutesName.StoryblokPage,
      query: { slug: storySlugFromInternalPath },
    });
    return;
  }

  void router.push(targetLink.value.path);
};
</script>

<style scoped>
.section-item {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  border: 1px solid var(--ion-color-primary);
  border-radius: var(--ion-radius-2xl);
  overflow: hidden;
}
.section-item-image {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: var(--ion-radius-2xl);
  object-fit: cover;
  grid-column: 1/2;
  grid-row: 1/2;
}
.section-item-title {
  font-weight: bold;
  text-align: start;
  color: white;
  align-self: self-end;
  grid-column: 1/2;
  grid-row: 1/2;
  padding: var(--ion-space-4);
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.9);
}
.with-background {
  background: rgba(0, 0, 0, 0.25);
}
</style>
